import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { GoogleAuthProvider, onIdTokenChanged, signInWithCredential, type User } from "firebase/auth";
import { readGoogleCallback, googleAuthUrl, STAFF_ROLES, type Role } from "@cop/domain";
import { auth, callable, errorMessage, TENANT } from "./firebase";

const PREVIEW_COOKIE = "cop_preview";
const previewToken = callable<Record<string, never>, { token: string }>("sitePreviewToken");

/** Staff browsers carry the edge pass, so team accounts see the real portal even while the site is closed. */
async function grantSitePreview() {
  if (location.protocol !== "https:") return;
  const { token } = await previewToken({});
  document.cookie = `${PREVIEW_COOKIE}=${token}; Path=/; Max-Age=${60 * 60 * 24 * 30}; SameSite=Lax; Secure`;
}
export function clearSitePreview() {
  document.cookie = `${PREVIEW_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax; Secure`;
}

const exchange = callable<{ code: string; codeVerifier: string; redirectUri: string }, { idToken: string }>("exchangeGoogleCode");

/** Full-page Google sign-in; Google sends the user back to the Admin root. */
export async function signInWithGoogle() {
  location.assign(await googleAuthUrl({ clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID ?? "", redirectUri: location.origin + import.meta.env.BASE_URL, returnTo: location.pathname }));
}

let completing: Promise<void> | null = null;
function completeGoogleRedirect() {
  return (completing ??= (async () => {
    const cb = readGoogleCallback(location.search);
    if (cb.kind === "none") return;
    history.replaceState(null, "", cb.kind === "invalid" ? "/" : cb.returnTo);
    if (cb.kind === "invalid") throw new Error("La sesión de ingreso venció. Probá de nuevo.");
    if (cb.kind === "cancelled") return;
    const { idToken } = await exchange({ code: cb.code, codeVerifier: cb.codeVerifier, redirectUri: cb.redirectUri });
    await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
  })());
}

interface Session {
  user: User | null;
  role: Role | null;
  practitionerId: string | null;
  isStaff: boolean;
  loading: boolean;
  authError?: string | null;
}

const Ctx = createContext<Session>({ user: null, role: null, practitionerId: null, isStaff: false, loading: true });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [s, setS] = useState<Session>({ user: null, role: null, practitionerId: null, isStaff: false, loading: true });
  const [finishing, setFinishing] = useState(() => new URLSearchParams(location.search).has("state"));
  const [authError, setAuthError] = useState<string | null>(null);
  useEffect(() => {
    completeGoogleRedirect()
      .catch((err) => setAuthError(errorMessage(err)))
      .finally(() => setFinishing(false));
  }, []);
  useEffect(
    () =>
      onIdTokenChanged(auth, async (user) => {
        if (!user) return setS({ user: null, role: null, practitionerId: null, isStaff: false, loading: false });
        const { claims } = await user.getIdTokenResult();
        const role = claims.tenantId === TENANT ? ((claims.role as Role) ?? null) : null;
        setS({
          user,
          role,
          practitionerId: (claims.practitionerId as string) ?? null,
          isStaff: !!role && (STAFF_ROLES as Role[]).includes(role),
          loading: false,
        });
        if (role && (STAFF_ROLES as Role[]).includes(role)) grantSitePreview().catch(() => undefined);
        else clearSitePreview();
      }),
    [],
  );
  return <Ctx.Provider value={{ ...s, loading: s.loading || finishing, authError }}>{children}</Ctx.Provider>;
}

export const useSession = () => useContext(Ctx);

export const ROLE_LABEL: Partial<Record<Role, string>> = {
  superadmin: "Superadmin",
  owner: "Dirección",
  branch_admin: "Administración",
  receptionist: "Recepción",
  dentist: "Odontología",
  specialist: "Especialista",
  assistant: "Asistente",
  billing: "Facturación",
  auditor: "Auditoría",
};
